/**
 * Renders the task's history in chronological order — triage classifications,
 * clarification Q&A pairs, plan, execute result, in the order they happened.
 *
 * Replaces the previous "all-triage-then-all-plan-then-all-clarifications"
 * layout, which buried the chronology and made multi-round clarifications
 * confusing.
 *
 * Inputs come from `task.recent_events`, which the API returns newest-first;
 * we flip to chronological. Events that aren't milestones (raw `ai_call`,
 * `transition` plumbing, `context_added`, etc.) are dropped — they're still
 * available in the Events tab below.
 */
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Markdown } from './Markdown';
import {
  Sparkles,
  ClipboardList,
  FileCheck2,
  MessageCircleQuestion,
  CheckCircle2,
  Pencil,
  XCircle,
  RotateCcw,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { TaskEventRecord } from '@agentflow/shared';

type TimelineItem =
  | {
      kind: 'triage';
      created_at: string;
      summary: string;
      meta: Record<string, unknown>;
    }
  | {
      kind: 'clarification';
      created_at: string; // when answer arrived
      question: string;
      reason?: string;
      answer: string;
    }
  | {
      kind: 'plan';
      created_at: string;
      content: string;
      meta: Record<string, unknown>;
      source: 'ai' | 'human_edit';
    }
  | {
      kind: 'approval';
      created_at: string;
      decision: 'approve' | 'edit' | 'request_changes' | 'reject';
      notes?: string;
      edited_plan?: string;
      requested_changes?: string;
    }
  | {
      kind: 'review';
      created_at: string;
      decision: 'accept' | 'request_changes';
      feedback?: string;
      notes?: string;
    }
  | {
      kind: 'execute_result';
      created_at: string;
      content: string;
      meta: Record<string, unknown>;
    };

function buildTimeline(events: TaskEventRecord[]): TimelineItem[] {
  // Events come newest-first; walk chronological for the timeline.
  const chrono = [...events].reverse();
  const out: TimelineItem[] = [];

  // Open clarification questions waiting for an answer. Keyed by question
  // text (good enough for our scale — you don't typically ask the same
  // question twice in one task).
  let pendingQ: { question: string; reason?: string } | null = null;

  for (const ev of chrono) {
    const p = (ev.payload ?? {}) as Record<string, unknown>;

    if (ev.type === 'ai_output') {
      if (p.kind === 'triage_classification' && typeof p.content === 'string') {
        const { kind: _k, content, ...meta } = p as { kind: string; content: string } & Record<string, unknown>;
        out.push({ kind: 'triage', created_at: ev.created_at, summary: content, meta });
      } else if (p.kind === 'plan' && typeof p.content === 'string') {
        const { kind: _k, content, ...meta } = p as { kind: string; content: string } & Record<string, unknown>;
        // Only the explicit `human_edit` source — produced when the user
        // saved their manual edits — gets the "adjusted by you" treatment.
        // AI-merged plans (request_changes) are tagged actor='ai' with no
        // source, so they render as regular plan cards.
        const metaSource = typeof meta.source === 'string' ? meta.source : undefined;
        const source: 'ai' | 'human_edit' =
          metaSource === 'human_edit' ? 'human_edit' : 'ai';
        out.push({ kind: 'plan', created_at: ev.created_at, content, meta, source });
      } else if (p.kind === 'execute_result' && typeof p.content === 'string') {
        const { kind: _k, content, ...meta } = p as { kind: string; content: string } & Record<string, unknown>;
        out.push({ kind: 'execute_result', created_at: ev.created_at, content, meta });
      }
      continue;
    }

    if (
      ev.type === 'pause_open' &&
      typeof p.question === 'string' &&
      typeof p.current_state === 'string'
    ) {
      pendingQ = {
        question: p.question,
        reason: typeof p.reason === 'string' ? p.reason : undefined,
      };
      continue;
    }

    if (ev.type === 'pause_resolve') {
      const res = (p.resolution ?? {}) as Record<string, unknown>;

      // Clarification — pair with the most recent open question.
      if (pendingQ && p.reason === 'clarification' && typeof res.answer === 'string') {
        out.push({
          kind: 'clarification',
          created_at: ev.created_at,
          question: pendingQ.question,
          reason: pendingQ.reason,
          answer: res.answer,
        });
        pendingQ = null;
        continue;
      }

      // Approval (approve / edit / request_changes / reject) — surface the
      // user's notes plus the edit body / requested changes when present.
      if (p.reason === 'approval' && typeof res.decision === 'string') {
        out.push({
          kind: 'approval',
          created_at: ev.created_at,
          decision: res.decision as 'approve' | 'edit' | 'request_changes' | 'reject',
          notes: typeof res.notes === 'string' ? res.notes : undefined,
          edited_plan: typeof res.edited_plan === 'string' ? res.edited_plan : undefined,
          requested_changes:
            typeof res.requested_changes === 'string' ? res.requested_changes : undefined,
        });
        continue;
      }

      // Review (accept / request_changes) — surface the user's feedback.
      if (p.reason === 'review' && typeof res.decision === 'string') {
        out.push({
          kind: 'review',
          created_at: ev.created_at,
          decision: res.decision as 'accept' | 'request_changes',
          feedback: typeof res.feedback === 'string' ? res.feedback : undefined,
          notes: typeof res.notes === 'string' ? res.notes : undefined,
        });
        continue;
      }
    }
  }

  return out;
}

/**
 * Optional in-place edit handle. When set, the most recent Plan card in the
 * timeline switches from a markdown render to a textarea bound to the
 * controlled value. Lets TaskDetailPage co-ordinate "Edit Manually" between
 * the pending-input panel (where the action button lives) and the timeline
 * (where the plan is rendered).
 */
export interface PlanEdit {
  value: string;
  onChange: (next: string) => void;
}

export function Timeline({
  events,
  planEdit,
}: {
  events: TaskEventRecord[];
  planEdit?: PlanEdit | null;
}): JSX.Element | null {
  const items = buildTimeline(events);
  if (items.length === 0) return null;

  // Identify the index of the most recent `plan` item so we can render
  // it as a textarea when planEdit is active. Walk backwards.
  let latestPlanIdx = -1;
  for (let i = items.length - 1; i >= 0; i--) {
    if (items[i]?.kind === 'plan') {
      latestPlanIdx = i;
      break;
    }
  }

  return (
    <div className="space-y-4">
      {items.map((item, i) => (
        <TimelineItemCard
          key={`${item.kind}-${item.created_at}-${i}`}
          item={item}
          planEdit={planEdit && i === latestPlanIdx ? planEdit : null}
        />
      ))}
    </div>
  );
}

function TimelineItemCard({
  item,
  planEdit,
}: {
  item: TimelineItem;
  planEdit?: PlanEdit | null;
}): JSX.Element {
  const when = formatDistanceToNow(new Date(item.created_at), { addSuffix: true });
  switch (item.kind) {
    case 'triage':
      return (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-muted-foreground" />
              Triage
              {typeof item.meta.difficulty === 'string' && item.meta.difficulty.length > 0 && (
                <Badge variant="outline" className="ml-1">
                  {String(item.meta.difficulty)}
                </Badge>
              )}
              <span className="ml-auto text-xs font-normal text-muted-foreground">{when}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-2">
            <p>{item.summary}</p>
            {item.meta.needs_clarification === true &&
              typeof item.meta.clarification_reason === 'string' && (
                <p className="text-xs text-muted-foreground italic">
                  {String(item.meta.clarification_reason)}
                </p>
              )}
          </CardContent>
        </Card>
      );

    case 'clarification':
      return (
        <Card className="border-amber-300/50 bg-amber-50/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <MessageCircleQuestion className="h-4 w-4 text-amber-700" />
              Clarification
              <span className="ml-auto text-xs font-normal text-muted-foreground">{when}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm space-y-1.5">
            {item.reason && (
              <p className="text-xs text-muted-foreground italic">{item.reason}</p>
            )}
            <div>
              <span className="text-xs font-medium text-muted-foreground">Q:</span>{' '}
              <span>{item.question}</span>
            </div>
            <div>
              <span className="text-xs font-medium text-muted-foreground">A:</span>{' '}
              <span>{item.answer}</span>
            </div>
          </CardContent>
        </Card>
      );

    case 'plan':
      return (
        <Card className={planEdit ? 'border-sky-300/60 ring-1 ring-sky-200/40' : undefined}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <ClipboardList className="h-4 w-4 text-muted-foreground" />
              Plan
              {item.source === 'human_edit' && !planEdit && (
                <Badge variant="outline" className="ml-1">
                  adjusted by you
                </Badge>
              )}
              {planEdit && (
                <Badge variant="default" className="ml-1">
                  editing — your changes will replace this plan
                </Badge>
              )}
              <span className="ml-auto text-xs font-normal text-muted-foreground">{when}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {planEdit ? (
              <Textarea
                rows={Math.max(8, item.content.split('\n').length)}
                value={planEdit.value}
                onChange={(e) => planEdit.onChange(e.target.value)}
                className="font-mono text-xs"
              />
            ) : (
              <Markdown>{item.content}</Markdown>
            )}
          </CardContent>
        </Card>
      );

    case 'approval':
      return <ApprovalCard item={item} when={when} />;

    case 'review':
      return <ReviewCard item={item} when={when} />;

    case 'execute_result':
      return (
        <Card className="border-emerald-300/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileCheck2 className="h-4 w-4 text-emerald-700" />
              Result
              <span className="ml-auto text-xs font-normal text-muted-foreground">{when}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Markdown>{item.content}</Markdown>
          </CardContent>
        </Card>
      );
  }
}

/**
 * Plan-approval resolution card. Renders distinctly per decision so the
 * Timeline reads naturally: "AI plan → you approved" / "you edited & approved" /
 * "you rejected with notes → AI re-planned".
 */
function ApprovalCard({
  item,
  when,
}: {
  item: Extract<TimelineItem, { kind: 'approval' }>;
  when: string;
}): JSX.Element {
  const Icon =
    item.decision === 'approve' ? CheckCircle2 :
    item.decision === 'edit' ? Pencil :
    item.decision === 'request_changes' ? RotateCcw :
    XCircle;
  const tone =
    item.decision === 'reject'
      ? 'border-destructive/40 bg-destructive/5'
      : item.decision === 'edit'
        ? 'border-sky-300/50 bg-sky-50/30'
        : item.decision === 'request_changes'
          ? 'border-amber-300/50 bg-amber-50/30'
          : 'border-emerald-300/50 bg-emerald-50/30';
  const label =
    item.decision === 'approve' ? 'You approved the plan' :
    item.decision === 'edit' ? 'Plan edited manually' :
    item.decision === 'request_changes' ? 'Changes requested' :
    'You rejected the plan';

  const hasBody =
    item.notes ||
    (item.decision === 'edit' && item.edited_plan) ||
    (item.decision === 'request_changes' && item.requested_changes);

  return (
    <Card className={tone}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="h-4 w-4" />
          {label}
          <span className="ml-auto text-xs font-normal text-muted-foreground">{when}</span>
        </CardTitle>
      </CardHeader>
      {hasBody && (
        <CardContent className="text-sm space-y-2">
          {item.decision === 'edit' && item.edited_plan && (
            <p className="text-xs text-muted-foreground">
              Your edited plan is shown below with the
              <Badge variant="outline" className="mx-1">adjusted by you</Badge>
              badge.
            </p>
          )}
          {item.decision === 'request_changes' && item.requested_changes && (
            <div>
              <div className="text-xs font-medium text-muted-foreground mb-1">
                Your requested changes:
              </div>
              <div className="rounded border bg-background/60 px-2 py-1.5 whitespace-pre-wrap">
                {item.requested_changes}
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                The updated plan is shown below for you to review before it executes.
              </p>
            </div>
          )}
          {item.notes && (
            <div>
              <span className="text-xs font-medium text-muted-foreground">Your notes:</span>{' '}
              <span>{item.notes}</span>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}

/**
 * Review resolution card — accepted / requested changes with feedback.
 */
function ReviewCard({
  item,
  when,
}: {
  item: Extract<TimelineItem, { kind: 'review' }>;
  when: string;
}): JSX.Element {
  const isAccept = item.decision === 'accept';
  const Icon = isAccept ? CheckCircle2 : RotateCcw;
  const tone = isAccept ? 'border-emerald-300/50 bg-emerald-50/30' : 'border-amber-300/50 bg-amber-50/30';
  const label = isAccept ? 'You accepted the result' : 'You requested changes';

  return (
    <Card className={tone}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Icon className="h-4 w-4" />
          {label}
          <span className="ml-auto text-xs font-normal text-muted-foreground">{when}</span>
        </CardTitle>
      </CardHeader>
      {(item.feedback || item.notes) && (
        <CardContent className="text-sm space-y-1">
          {item.feedback && (
            <div>
              <span className="text-xs font-medium text-muted-foreground">Feedback:</span>{' '}
              <span>{item.feedback}</span>
            </div>
          )}
          {item.notes && (
            <div>
              <span className="text-xs font-medium text-muted-foreground">Notes:</span>{' '}
              <span>{item.notes}</span>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
